/**
 * 食材与营养字典。
 *
 * 这是本地唯一需要维护的"数据资产"。刻意**不是菜谱库** ——
 * 菜谱交给 AI 现场生成，本地只负责给 AI 立规矩：
 *   · 品类划分（便于分类展示与 prompt 约束）
 *   · 默认单位（用户输入"猪肉 2"时知道该是"斤"还是"克"）
 *   · 别名归一（"西红柿/番茄/tomato" → 番茄）
 *
 * 刻意**不含保质期**：临期判断已经被整个去掉了，
 * 现在只记"这东西放了几天"，由用户自己说了算，不靠字典猜。
 *
 * 新增食材只需往 FOOD_DICTIONARY 加一行。
 */

import type { FoodCategory } from './contracts';

export interface FoodEntry {
  /** 规范名 */
  name: string;
  category: FoodCategory;
  /** 首选单位，按优先级排列 */
  units: string[];
  /** 别名，用于归一化 */
  aliases?: string[];
}

export const FOOD_DICTIONARY: FoodEntry[] = [
  /* ---------------- 蔬菜 ---------------- */
  { name: '番茄', category: '蔬菜', units: ['个', '斤'], aliases: ['西红柿', 'tomato'] },
  { name: '黄瓜', category: '蔬菜', units: ['根', '斤'], aliases: ['青瓜'] },
  { name: '土豆', category: '蔬菜', units: ['个', '斤'], aliases: ['马铃薯', '洋芋'] },
  { name: '胡萝卜', category: '蔬菜', units: ['根', '斤'], aliases: ['红萝卜'] },
  { name: '白萝卜', category: '蔬菜', units: ['根', '斤'] },
  { name: '洋葱', category: '蔬菜', units: ['个', '斤'] },
  { name: '大蒜', category: '蔬菜', units: ['头', '斤'], aliases: ['蒜', '蒜头'] },
  { name: '生姜', category: '蔬菜', units: ['块', '斤'], aliases: ['姜', '姜片'] },
  { name: '小葱', category: '蔬菜', units: ['把', '根'], aliases: ['香葱', '葱'] },
  { name: '大葱', category: '蔬菜', units: ['根', '斤'] },
  { name: '香菜', category: '蔬菜', units: ['把'], aliases: ['芫荽'] },
  { name: '青椒', category: '蔬菜', units: ['个', '斤'], aliases: ['柿子椒', '灯笼椒'] },
  { name: '尖椒', category: '蔬菜', units: ['个', '斤'], aliases: ['线椒'] },
  { name: '小米辣', category: '蔬菜', units: ['个', '把'], aliases: ['朝天椒', '小米椒'] },
  { name: '菠菜', category: '蔬菜', units: ['把', '斤'] },
  { name: '生菜', category: '蔬菜', units: ['颗', '斤'], aliases: ['球生菜'] },
  { name: '小白菜', category: '蔬菜', units: ['把', '斤'], aliases: ['青菜', '上海青'] },
  { name: '大白菜', category: '蔬菜', units: ['颗', '斤'] },
  { name: '娃娃菜', category: '蔬菜', units: ['颗'] },
  { name: '卷心菜', category: '蔬菜', units: ['颗', '斤'], aliases: ['包菜', '圆白菜'] },
  { name: '西兰花', category: '蔬菜', units: ['颗', '斤'], aliases: ['绿花菜'] },
  { name: '菜花', category: '蔬菜', units: ['颗', '斤'], aliases: ['花菜', '白花菜'] },
  { name: '芹菜', category: '蔬菜', units: ['把', '斤'], aliases: ['西芹'] },
  { name: '韭菜', category: '蔬菜', units: ['把', '斤'] },
  { name: '茄子', category: '蔬菜', units: ['个', '斤'], aliases: ['长茄'] },
  { name: '南瓜', category: '蔬菜', units: ['块', '斤'] },
  { name: '冬瓜', category: '蔬菜', units: ['块', '斤'] },
  { name: '丝瓜', category: '蔬菜', units: ['根', '斤'] },
  { name: '莲藕', category: '蔬菜', units: ['节', '斤'], aliases: ['藕'] },
  { name: '玉米', category: '蔬菜', units: ['根', '个'], aliases: ['甜玉米'] },
  { name: '山药', category: '蔬菜', units: ['根', '斤'] },
  { name: '香菇', category: '蔬菜', units: ['个', '斤'], aliases: ['鲜香菇'] },
  { name: '金针菇', category: '蔬菜', units: ['包', '把'] },
  { name: '杏鲍菇', category: '蔬菜', units: ['个', '斤'] },
  { name: '平菇', category: '蔬菜', units: ['斤'] },
  { name: '豆芽', category: '蔬菜', units: ['斤', '把'], aliases: ['黄豆芽', '绿豆芽'] },
  { name: '秋葵', category: '蔬菜', units: ['斤'] },
  { name: '芦笋', category: '蔬菜', units: ['把', '斤'] },
  { name: '荷兰豆', category: '蔬菜', units: ['斤'] },
  { name: '四季豆', category: '蔬菜', units: ['斤'], aliases: ['豆角', '芸豆'] },
  { name: '青豆', category: '蔬菜', units: ['斤'], aliases: ['豌豆'] },
  { name: '苦瓜', category: '蔬菜', units: ['根', '斤'] },
  { name: '红薯', category: '蔬菜', units: ['个', '斤'], aliases: ['地瓜', '番薯'] },

  /* ---------------- 水果 ---------------- */
  { name: '苹果', category: '水果', units: ['个', '斤'] },
  { name: '香蕉', category: '水果', units: ['根', '把'] },
  { name: '橙子', category: '水果', units: ['个', '斤'], aliases: ['橙'] },
  { name: '柑橘', category: '水果', units: ['个', '斤'], aliases: ['橘子', '桔子', '砂糖橘'] },
  { name: '柠檬', category: '水果', units: ['个'] },
  { name: '梨', category: '水果', units: ['个', '斤'], aliases: ['雪梨'] },
  { name: '葡萄', category: '水果', units: ['串', '斤'] },
  { name: '草莓', category: '水果', units: ['盒', '斤'] },
  { name: '蓝莓', category: '水果', units: ['盒'] },
  { name: '猕猴桃', category: '水果', units: ['个'], aliases: ['奇异果'] },
  { name: '西瓜', category: '水果', units: ['个', '块'] },
  { name: '牛油果', category: '水果', units: ['个'], aliases: ['鳄梨'] },
  { name: '芒果', category: '水果', units: ['个'] },

  /* ---------------- 肉类 ---------------- */
  { name: '猪肉', category: '肉类', units: ['斤', '克'], aliases: ['猪瘦肉', '里脊'] },
  { name: '五花肉', category: '肉类', units: ['斤', '克'], aliases: ['猪五花'] },
  { name: '猪排骨', category: '肉类', units: ['斤', '克'], aliases: ['排骨', '肋排'] },
  { name: '猪肉末', category: '肉类', units: ['斤', '克'], aliases: ['肉馅', '猪绞肉'] },
  { name: '牛肉', category: '肉类', units: ['斤', '克'], aliases: ['牛里脊', '牛腩'] },
  { name: '牛腩', category: '肉类', units: ['斤', '克'] },
  { name: '鸡肉', category: '肉类', units: ['斤', '克'], aliases: ['整鸡'] },
  { name: '鸡胸肉', category: '肉类', units: ['块', '斤'], aliases: ['鸡胸'] },
  { name: '鸡腿', category: '肉类', units: ['个', '斤'], aliases: ['鸡腿肉'] },
  { name: '鸡翅', category: '肉类', units: ['个', '斤'], aliases: ['鸡翅中'] },
  { name: '羊肉', category: '肉类', units: ['斤', '克'], aliases: ['羊腿肉'] },
  { name: '培根', category: '肉类', units: ['包', '片'] },
  { name: '香肠', category: '肉类', units: ['根', '包'], aliases: ['腊肠'] },
  { name: '火腿', category: '肉类', units: ['包', '片'], aliases: ['火腿片'] },

  /* ---------------- 水产 ---------------- */
  { name: '基围虾', category: '水产', units: ['斤', '只'], aliases: ['虾', '鲜虾', '白虾'] },
  { name: '虾仁', category: '水产', units: ['包', '斤'] },
  { name: '鲈鱼', category: '水产', units: ['条'], aliases: ['鱼'] },
  { name: '草鱼', category: '水产', units: ['条'] },
  { name: '三文鱼', category: '水产', units: ['块', '斤'], aliases: ['鲑鱼'] },
  { name: '带鱼', category: '水产', units: ['斤', '条'] },
  { name: '鱿鱼', category: '水产', units: ['斤'] },
  { name: '蛤蜊', category: '水产', units: ['斤'], aliases: ['花蛤'] },
  { name: '海带', category: '水产', units: ['斤', '把'], aliases: ['海带结'] },

  /* ---------------- 蛋奶 ---------------- */
  { name: '鸡蛋', category: '蛋奶', units: ['个', '盒'], aliases: ['蛋', '土鸡蛋'] },
  { name: '鸭蛋', category: '蛋奶', units: ['个'] },
  { name: '咸鸭蛋', category: '蛋奶', units: ['个'] },
  { name: '牛奶', category: '蛋奶', units: ['盒', '升'], aliases: ['纯牛奶'] },
  { name: '酸奶', category: '蛋奶', units: ['盒', '杯'] },
  { name: '黄油', category: '蛋奶', units: ['块', '克'], aliases: ['牛油'] },
  { name: '淡奶油', category: '蛋奶', units: ['盒'], aliases: ['奶油'] },
  { name: '芝士', category: '蛋奶', units: ['片', '包'], aliases: ['奶酪', '芝士片'] },

  /* ---------------- 豆制品 ---------------- */
  { name: '豆腐', category: '豆制品', units: ['块', '盒'], aliases: ['老豆腐', '嫩豆腐'] },
  { name: '豆干', category: '豆制品', units: ['包', '块'], aliases: ['香干'] },
  { name: '豆腐皮', category: '豆制品', units: ['包', '张'], aliases: ['千张', '干豆腐'] },
  { name: '腐竹', category: '豆制品', units: ['包'] },
  { name: '豆浆', category: '豆制品', units: ['杯', '升'] },

  /* ---------------- 主食 ---------------- */
  { name: '大米', category: '主食', units: ['袋', '斤'], aliases: ['米'] },
  { name: '挂面', category: '主食', units: ['包', '把'], aliases: ['面条', '面'] },
  { name: '饺子皮', category: '主食', units: ['包', '张'] },
  { name: '馒头', category: '主食', units: ['个', '袋'] },
  { name: '吐司', category: '主食', units: ['袋', '片'], aliases: ['面包', '切片面包'] },
  { name: '粉丝', category: '主食', units: ['包', '把'], aliases: ['龙口粉丝'] },
  { name: '年糕', category: '主食', units: ['包', '块'] },

  /* ---------------- 调料 ---------------- */
  { name: '食用油', category: '调料', units: ['瓶', '升'], aliases: ['菜籽油', '花生油', '调和油'] },
  { name: '生抽', category: '调料', units: ['瓶'], aliases: ['酱油', '鲜味生抽'] },
  { name: '老抽', category: '调料', units: ['瓶'] },
  { name: '蚝油', category: '调料', units: ['瓶'] },
  { name: '料酒', category: '调料', units: ['瓶'], aliases: ['黄酒'] },
  { name: '香醋', category: '调料', units: ['瓶'], aliases: ['醋', '陈醋', '米醋'] },
  { name: '白糖', category: '调料', units: ['袋'], aliases: ['糖'] },
  { name: '食盐', category: '调料', units: ['袋'], aliases: ['盐'] },
  { name: '白胡椒粉', category: '调料', units: ['瓶'], aliases: ['胡椒粉', '胡椒'] },
  { name: '花椒', category: '调料', units: ['袋'] },
  { name: '干辣椒', category: '调料', units: ['袋'], aliases: ['辣椒干'] },
  { name: '豆瓣酱', category: '调料', units: ['瓶', '袋'], aliases: ['郫县豆瓣'] },
  { name: '番茄酱', category: '调料', units: ['瓶'], aliases: ['蕃茄酱'] },
  { name: '淀粉', category: '调料', units: ['袋'], aliases: ['玉米淀粉', '生粉'] },
  { name: '芝麻油', category: '调料', units: ['瓶'], aliases: ['香油'] },
  { name: '咖喱块', category: '调料', units: ['盒'], aliases: ['咖喱'] },
  { name: '黑胡椒酱', category: '调料', units: ['瓶'] },

  /* ---------------- 干货 / 冷冻 / 熟食 ---------------- */
  { name: '干香菇', category: '干货', units: ['袋', '把'] },
  { name: '木耳', category: '干货', units: ['袋'], aliases: ['黑木耳'] },
  { name: '红枣', category: '干货', units: ['袋'], aliases: ['大枣'] },
  { name: '枸杞', category: '干货', units: ['袋'] },
  { name: '花生米', category: '干货', units: ['袋', '斤'], aliases: ['花生'] },
  { name: '紫菜', category: '干货', units: ['包'] },
  { name: '虾皮', category: '干货', units: ['袋'] },
  { name: '速冻水饺', category: '冷冻', units: ['包'], aliases: ['水饺', '饺子'] },
  { name: '速冻汤圆', category: '冷冻', units: ['包'], aliases: ['汤圆'] },
  { name: '冷冻鸡块', category: '冷冻', units: ['包'], aliases: ['炸鸡块'] },
  { name: '冰淇淋', category: '冷冻', units: ['盒'], aliases: ['雪糕'] },
  { name: '即食鸡胸', category: '熟食', units: ['包'] },
  { name: '卤牛肉', category: '熟食', units: ['包', '斤'] },
  { name: '榨菜', category: '熟食', units: ['包'] },
];

/* ------------------------------------------------------------------ */
/* 派生逻辑                                                            */
/* ------------------------------------------------------------------ */

/** 建立别名索引，加速归一化查询。模块加载时构建一次。 */
const ALIAS_INDEX: Map<string, FoodEntry> = (() => {
  const index = new Map<string, FoodEntry>();
  for (const entry of FOOD_DICTIONARY) {
    index.set(entry.name, entry);
    for (const alias of entry.aliases ?? []) index.set(alias, entry);
  }
  return index;
})();

/**
 * 把用户/AI 给的名字归一化成字典里的规范名。
 * 找不到时退回清理后的原名 —— **不丢数据**，只是无法享受字典的默认值。
 */
export function normalizeFoodName(raw: string): string {
  const cleaned = raw
    .trim()
    .replace(/\s+/g, '')
    .replace(/^(新鲜|有机|精品|进口|本地)/, '')
    .replace(/(适量|若干|一点|少许)$/, '');
  if (!cleaned) return raw.trim();

  const hit = ALIAS_INDEX.get(cleaned);
  if (hit) return hit.name;

  // 允许"猪里脊肉""生鸡蛋"这类前后缀变体命中
  for (const [alias, entry] of ALIAS_INDEX) {
    if (alias.length >= 2 && (cleaned.includes(alias) || alias.includes(cleaned))) {
      return entry.name;
    }
  }
  return cleaned;
}

export function lookupFood(name: string): FoodEntry | undefined {
  return ALIAS_INDEX.get(normalizeFoodName(name));
}

export function guessCategory(name: string): FoodCategory {
  return lookupFood(name)?.category ?? '其他';
}

export function defaultUnitFor(name: string): string {
  return lookupFood(name)?.units[0] ?? '份';
}

export function unitOptionsFor(name: string): string[] {
  return lookupFood(name)?.units ?? ['份', '个', '斤', '克', '包', '瓶'];
}

/** 供前端做输入联想 / 后端做 prompt 约束用的精简清单 */
export function foodNameList(): string[] {
  return FOOD_DICTIONARY.map((e) => e.name);
}
